import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, isAdmin } from '../utils/userAuth';
import { getServices, addService, deleteService, getAllServicesAdmin, adminDeleteListing } from '../utils/listingsStore';
import './ServiceList.css';

function ServiceList() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeTab, setActiveTab] = useState('browse'); // browse, my-listings, admin
  const [user, setUser] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    search: '',
    sort: 'createdAt'
  });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'tutoring',
    price: '',
    priceType: 'fixed',
    location: 'SPU Campus'
  });

  useEffect(() => {
    const currentUser = getCurrentUser();
    setUser(currentUser);
    fetchServices();
  }, [filters, activeTab]);

  const fetchServices = () => {
    setLoading(true);
    try {
      let data;
      if (activeTab === 'admin' && user && isAdmin(user.email)) {
        data = getAllServicesAdmin();
      } else if (activeTab === 'my-listings' && user) {
        data = getServices(filters).filter(s => s.provider?.id === user.id);
      } else {
        data = getServices(filters);
      }
      setServices(data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      addService({
        ...formData,
        price: parseFloat(formData.price)
      }, user);

      setFormData({
        title: '',
        description: '',
        category: 'tutoring',
        price: '',
        priceType: 'fixed',
        location: 'SPU Campus'
      });
      setShowAddForm(false);
      fetchServices();
    } catch (error) {
      console.error('Error adding service:', error);
    }
  };

  const handleDelete = (serviceId) => {
    if (!user) return;

    if (window.confirm('Are you sure you want to delete this listing?')) {
      if (activeTab === 'admin' && isAdmin(user.email)) {
        adminDeleteListing('services', serviceId);
      } else {
        deleteService(serviceId, user.id);
      }
      fetchServices();
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'rides': '#4CAF50',
      'tutoring': '#2196F3',
      'errands': '#FF9800',
      'campus-tasks': '#9C27B0',
      'marketplace': '#E91E63',
      'rentals': '#00BCD4'
    };
    return colors[category] || '#666';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'rides': '🚗',
      'tutoring': '📚',
      'errands': '🛒',
      'campus-tasks': '🏗️',
      'marketplace': '🛍️',
      'rentals': '🔑'
    };
    return icons[category] || '📦';
  };

  return (
    <div className="service-list-page">
      {/* Header */}
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        <h1 className="page-title">Services</h1>
        <p className="page-subtitle">Offer or find services on campus</p>
      </div>

      {/* Tabs */}
      <div className="listing-tabs">
        <button
          className={`tab-btn ${activeTab === 'browse' ? 'active' : ''}`}
          onClick={() => setActiveTab('browse')}
        >
          Browse All
        </button>
        {user && (
          <button
            className={`tab-btn ${activeTab === 'my-listings' ? 'active' : ''}`}
            onClick={() => setActiveTab('my-listings')}
          >
            My Listings
          </button>
        )}
        {user && isAdmin(user.email) && (
          <button
            className={`tab-btn admin-tab ${activeTab === 'admin' ? 'active' : ''}`}
            onClick={() => setActiveTab('admin')}
          >
            Admin
          </button>
        )}
      </div>

      {/* Add Button */}
      {user && (
        <button className="fab-add-btn" onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? '✕' : '+'}
        </button>
      )}

      {/* Quick Add Form */}
      {showAddForm && user && (
        <div className="quick-add-form">
          <h3>List a New Service</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <input
                type="text"
                name="title"
                placeholder="Service title (e.g., Math Tutoring)"
                value={formData.title}
                onChange={handleFormChange}
                required
              />
            </div>
            <div className="form-row">
              <textarea
                name="description"
                placeholder="Describe your service..."
                value={formData.description}
                onChange={handleFormChange}
                required
              />
            </div>
            <div className="form-row two-col">
              <select name="category" value={formData.category} onChange={handleFormChange}>
                <option value="tutoring">Tutoring</option>
                <option value="errands">Errands</option>
                <option value="campus-tasks">Campus Tasks</option>
                <option value="other">Other</option>
              </select>
              <div className="price-input">
                <span className="currency">$</span>
                <input
                  type="number"
                  name="price"
                  placeholder="Price"
                  value={formData.price}
                  onChange={handleFormChange}
                  required
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div className="form-row two-col">
              <select name="priceType" value={formData.priceType} onChange={handleFormChange}>
                <option value="fixed">Fixed Price</option>
                <option value="hourly">Per Hour</option>
                <option value="negotiable">Negotiable</option>
              </select>
              <input
                type="text"
                name="location"
                placeholder="Location"
                value={formData.location}
                onChange={handleFormChange}
              />
            </div>
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button type="submit" className="btn-submit">
                Post Service
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <input
            type="text"
            name="search"
            placeholder="Search services..."
            value={filters.search}
            onChange={handleFilterChange}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <select name="category" value={filters.category} onChange={handleFilterChange}>
            <option value="">All Categories</option>
            <option value="tutoring">Tutoring</option>
            <option value="errands">Errands</option>
            <option value="campus-tasks">Campus Tasks</option>
          </select>
        </div>
        <div className="filter-group">
          <select name="sort" value={filters.sort} onChange={handleFilterChange}>
            <option value="createdAt">Newest First</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
          </select>
        </div>
      </div>

      {/* Services Grid */}
      {loading ? (
        <div className="loading-message">Loading services...</div>
      ) : services.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No services found</h3>
          <p>{activeTab === 'my-listings' ? 'You haven\'t posted any services yet.' : 'Be the first to offer a service!'}</p>
          {user && (
            <button className="btn-primary" onClick={() => setShowAddForm(true)}>
              Post a Service
            </button>
          )}
        </div>
      ) : (
        <div className="services-grid">
          {services.map(service => (
            <div key={service._id} className="service-card">
              <div className="service-header">
                <span
                  className="service-category"
                  style={{ backgroundColor: getCategoryColor(service.category) }}
                >
                  {getCategoryIcon(service.category)} {service.category?.replace('-', ' ')}
                </span>
                {(activeTab === 'my-listings' || activeTab === 'admin') && (
                  <button
                    className="delete-btn"
                    onClick={() => handleDelete(service._id)}
                  >
                    🗑️
                  </button>
                )}
              </div>

              <h3 className="service-title">{service.title}</h3>
              <p className="service-description">{service.description}</p>

              <div className="service-info">
                <div className="service-provider">
                  By: {service.provider?.name || 'Student'}
                </div>
                <div className="service-location">
                  📍 {service.location}
                </div>
              </div>

              <div className="service-footer">
                <div className="service-price">
                  ${service.price}
                  {service.priceType === 'hourly' && '/hr'}
                  {service.priceType === 'negotiable' && ' (negotiable)'}
                </div>
                <button className="btn-contact">Contact</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Login Prompt */}
      {!user && (
        <div className="login-prompt">
          <p>Want to offer your services?</p>
          <button className="btn-primary" onClick={() => navigate('/login')}>
            Sign in to post
          </button>
        </div>
      )}
    </div>
  );
}

export default ServiceList;
